import produce from 'immer';
import { registerReducer } from '../../../StateSetup/RootReducer';
import JWT from 'jsonwebtoken';
import { APP_URL } from '../../../utils/AppConstants';

const BFF_BASE_URL = APP_URL.GB_BFF_HOST;
const OK = 200;
export const EXTERNAL_SERVICE_GET_AEM_ERROR_CODE = 'E_MEMBER_PROFILE_G_0001';

// Action names
const ACTION_NAME_PREFIX = 'action/getAEMContent';
export const REQUEST_ACTION_NAME = `${ACTION_NAME_PREFIX}/request`;
export const RESPONSE_ACTION_NAME = `${ACTION_NAME_PREFIX}/response`;
export const ERROR_ACTION_NAME = `${ACTION_NAME_PREFIX}/error`;

/**
 * Gets AEM content from BFF based on group, location, and plan. Persists content in state.
 * @param {object} logger
 */
export const GetAEMContent = () => {
    return async (dispatch, getState, { logger }) => {
        dispatch({ type: REQUEST_ACTION_NAME });

        const { jwt, language, sessionCorrelationId } = getState().Authentication;
        const jwtData = JWT.decode(jwt);
        const { clientNumber, locationNum, planNum } = jwtData.Identity;
        const transactionId = logger.generateGuid();

        logger.info(
            `${ACTION_NAME_PREFIX}: Fetching AEM Content. (clientNumber: ${clientNumber}, locationNum: ${locationNum}, planNum: ${planNum})`,
            {
                correlation: { transactionId },
            }
        );

        /**
         * Fetches AEM content from BFF from the appropriate namespace.
         * Note: To fetch AEM content that has not been assigned a namespace (e.g. MMB content),
         * simply pass an empty string '' as the argument like so fetchAEMContent('')
         * @param {string} namespace
         */
        const fetchAEMContent = async (namespace) => {
            const url = `${BFF_BASE_URL}/aem/v2/content?locale=${language}&namespace=${namespace}&hierarchies=${clientNumber}/${planNum
                .toString()
                .toLowerCase()}/${locationNum}`;
            const headers = {
                'Content-Type': 'application/json',
                'mli-session-correlation-id': sessionCorrelationId,
                'mli-correlation-id': transactionId,
                'mli-channel': 'web',
            };

            const response = await fetch(url, { headers });
            return response;
        };

        const namespaces = ['gb-memberprofile', 'gb-shared', 'gb-memberexperience', 'gb-cobranding'];

        try {
            // All AEM requests are fulfilled concurrently
            const responses = await Promise.all(namespaces.map((ns) => fetchAEMContent(ns)));

            const contents = await Promise.all(responses?.map((response) => response.json()));

            let finalContent = {};
            responses.forEach((response, index) => {
                const content = contents[index];
                const namespace = namespaces[index];

                if (response.status !== OK) {
                    const errorMessage = `Failed to fetch AEM ${namespace} data. Code: ${content.code} Reason: ${content.message}`;
                    throw new Error(errorMessage);
                }

                finalContent = { ...finalContent, ...content };
            });

            return dispatch({
                type: RESPONSE_ACTION_NAME,
                data: finalContent,
                success: true,
            });
        } catch (error) {
            logger.critical(`${ACTION_NAME_PREFIX}: Fail to fetch AEM content. ${error.message}`, {
                correlation: { transactionId },
            });

            return dispatch({
                type: ERROR_ACTION_NAME,
                code: EXTERNAL_SERVICE_GET_AEM_ERROR_CODE,
                message: 'An unexpected error occurred while fetching AEM content.',
            });
        }
    };
};

/**
 * Fired off when the request is initially sent to provide the loading state
 * @param {object} state
 * @param {object} action
 */
export const GetAEMContentRequestReducer = produce((state) => {
    state.AEMContent.isLoading = true;
});

/**
 * Handles a successful response of AEM content
 * @param {object} state
 * @param {object} action
 */
export const GetAEMContentResponseReducer = produce((state, action) => {
    state.AEMContent.isLoading = false;
    state.AEMContent.data = action.data;
});

/**
 * Fired off when the request encounters an error. An error thrown by this part of the code is considered a critical
 * failure.
 * @param {object} state
 * @param {object} action
 */
export const GetAEMContentErrorReducer = produce((state, action) => {
    state.AEMContent.isLoading = false;
    state.Errors.initialLoad.hasError = true;
    state.Errors.initialLoad.code = action.code;
    state.Errors.initialLoad.message = action.message;
});

// Register Reducers with Redux root reducer
registerReducer(REQUEST_ACTION_NAME, GetAEMContentRequestReducer);
registerReducer(RESPONSE_ACTION_NAME, GetAEMContentResponseReducer);
registerReducer(ERROR_ACTION_NAME, GetAEMContentErrorReducer);
